import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { useAuth } from '@/middlewares/auth/auth-context'
import { hasConsent } from '@/components/CookiesConsent/index.utils'
import { isPreviewPage } from '@/components/AppVersion/version.utils'
import { SparkleIcon, WHATS_NEW_EVENT } from './NewDesignChip'

import './index.css'

const SEEN_KEY = 'sf-welcome'

/** Fired on `window` when the card is dismissed: the organizer guided tour waits for it (Organizer/Tours). */
export const WELCOME_DONE_EVENT = 'scoring:welcome-done'

const seen = () => {
    try {
        return !!localStorage.getItem(SEEN_KEY)
    } catch {
        return false
    }
}

export const isWelcomeSeen = seen

/**
 * One-time welcome card for the redesign (mock `#rdWelcome`): same corner and layer as the cookies
 * card, so it waits until the visitor has answered the cookies banner (the two never coexist).
 * "Got it" / "Learn more" (→ opens the nav chip's popover) both write `sf-welcome`.
 */
export default function DesignTransitionWelcome() {
    const { t } = useTranslation(TranslationNS.DESIGN_TRANSITION)
    const { cookiesConsentOpen } = useAuth()
    const location = useLocation()

    const [done, setDone] = useState(seen)
    const show = !done && !cookiesConsentOpen && hasConsent() && !isPreviewPage(location.pathname)

    useEffect(() => {
        document.body.classList.toggle('welcome-open', show)
        return () => document.body.classList.remove('welcome-open')
    }, [show])

    if (!show) return null

    const dismiss = (learnMore) => {
        try {
            localStorage.setItem(SEEN_KEY, 'seen')
        } catch {
            /* private mode: the card simply comes back next visit */
        }
        setDone(true)
        window.dispatchEvent(new CustomEvent(WELCOME_DONE_EVENT))
        if (learnMore) window.dispatchEvent(new CustomEvent(WHATS_NEW_EVENT))
    }

    return (
        <div className="rd-card" role="region" aria-labelledby="rdWelcomeTtl">
            <div className="rd-head">
                <SparkleIcon className="rd-ico" />
                <p className="rd-ttl" id="rdWelcomeTtl">
                    {t('Welcome to the new scoring.fit')}
                </p>
            </div>
            <p className="rd-txt">
                {t(
                    "We're rolling out the new design page by page. Some pages still use the previous interface: that's not a bug, they will follow.",
                )}
            </p>
            <div className="rd-acts">
                <button type="button" className="rd-btn" onClick={() => dismiss(false)}>
                    {t('Got it')}
                </button>
                <button type="button" className="rd-link" onClick={() => dismiss(true)}>
                    {t('Learn more')}
                </button>
            </div>
        </div>
    )
}
